/**
 * Match account detail page.
 *
 * Mobile-first layout:
 *   - Sticky summary for balance, split count, and account state.
 *   - Quick action sheet for ledger access and route context.
 *   - Account info and split rules stacked as grouped native sections.
 *   - Recent activity rendered as cards on phones and a compact table on desktop.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  ExternalLink,
  ScrollText,
  Trash2,
} from 'lucide-react';
import {
  api,
  ApiError,
  match,
  type MatchAccount,
  type MatchAccountDetail,
  type MatchLedgerRow,
  type MatchSplit,
  type Party,
  type SplitReplaceInput,
} from '../api';
import { useReadOnly } from '../ui/AppShell';
import { MatchShell } from './MatchShell';
import { usePinGate } from '../auth/pinGate';
import { ActionQueueCard, MetricStrip, StickySummaryBar, TimelineRow } from '../ui/mobileOps';
import {
  Btn,
  EmptyState,
  RowSkeleton,
  SectionCard,
  StatePill,
} from '../ui/components';
import {
  Button,
  ErrorBanner,
  Pill,
  Select,
  Spinner,
  TextInput,
} from '../ui/primitives';

interface DraftSplit {
  key: string;
  targetMatchAccountId: number | null;
  label: string;
  percentage: string;
}

export function MatchAccountDetailPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const valid = Number.isFinite(id);

  const qc = useQueryClient();
  const nav = useNavigate();
  const { withPin } = usePinGate();
  const sectionReadOnly = useReadOnly();

  const detailQ = useQuery<MatchAccountDetail>({
    queryKey: ['match-account', id],
    queryFn: () => match.getAccount(id),
    enabled: valid,
  });

  const recentQ = useQuery<{ rows: MatchLedgerRow[] }>({
    queryKey: ['match-ledger-recent', id],
    queryFn: () => match.getLedger(id, { limit: 10 }),
    enabled: valid,
  });

  const accountsQ = useQuery<MatchAccount[]>({
    queryKey: ['match-accounts', 'all'],
    queryFn: () => match.listAccounts({ status: 'all' }),
  });

  const partiesQ = useQuery<Party[]>({
    queryKey: ['parties', 'all'],
    queryFn: () => api.get<Party[]>('/api/parties'),
  });

  const [pageError, setPageError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState(false);

  const archiveMut = useMutation({
    mutationFn: () => match.archiveAccount(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['match-account', id] });
      void qc.invalidateQueries({ queryKey: ['match-accounts'] });
      void qc.invalidateQueries({ queryKey: ['match-balances'] });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 423) return;
      setPageError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const unarchiveMut = useMutation({
    mutationFn: () => match.unarchiveAccount(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['match-account', id] });
      void qc.invalidateQueries({ queryKey: ['match-accounts'] });
      void qc.invalidateQueries({ queryKey: ['match-balances'] });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 423) return;
      setPageError(e instanceof ApiError ? e.message : String(e));
    },
  });

  if (!valid) {
    return (
      <MatchShell>
        <div className="p-4">
          <ErrorBanner>Not a valid match account id.</ErrorBanner>
        </div>
      </MatchShell>
    );
  }

  if (detailQ.isLoading || !detailQ.data) {
    return (
      <MatchShell>
        <div className="py-4">
          {detailQ.isError ? (
            <ErrorBanner>
              {detailQ.error instanceof ApiError
                ? detailQ.error.message
                : 'Failed to load match account.'}
            </ErrorBanner>
          ) : (
            <RowSkeleton rows={4} />
          )}
        </div>
      </MatchShell>
    );
  }

  const { account, splits, balance } = detailQ.data;
  const linkedParty =
    account.linkedPartyId !== null
      ? partiesQ.data?.find((p) => p.id === account.linkedPartyId)
      : null;
  const readOnly = sectionReadOnly || account.status === 'archived';
  const mutating = archiveMut.isPending || unarchiveMut.isPending;
  const configuredPct = Math.round(
    splits.reduce((sum, split) => sum + Number(split.percentage || '0'), 0) * 1e6,
  ) / 1e6;
  const remainderPct = Math.round((100 - configuredPct) * 1e6) / 1e6;
  const accountsById = useMemo(() => {
    const all = [account, ...(accountsQ.data ?? []).filter((item) => item.id !== account.id)];
    return new Map(all.map((item) => [item.id, item]));
  }, [account, accountsQ.data]);

  return (
    <MatchShell title={account.name} subtitle="Routing rules and recent posting history" back={{ to: '/match/accounts', label: 'Accounts' }}>
      <div className="space-y-4">
        <div className="md:hidden">
          <StickySummaryBar
            section="match"
            stackedBelowTabs
            title={account.name}
            subtitle={
              linkedParty
                ? `${linkedParty.displayName} · ${linkedParty.role}`
                : 'Independent match account'
            }
            metrics={[
              {
                label: 'Balance',
                value: balance,
                tone: balance.startsWith('-') ? 'neg' : balance === '0' ? 'default' : 'pos',
              },
              {
                label: 'Splits',
                value: String(splits.length),
                tone: splits.length === 0 ? 'warn' : 'default',
              },
              {
                label: 'State',
                value: account.status === 'active' ? 'Live' : 'Archived',
                tone: account.status === 'active' ? 'pos' : 'warn',
              },
            ]}
            actions={
              <>
                <Btn size="sm" variant="secondary" onClick={() => nav(`/match/accounts/${id}/ledger`)}>
                  Ledger
                </Btn>
                {!sectionReadOnly && account.status === 'active' && (
                  <Btn
                    size="sm"
                    variant="secondary"
                    disabled={mutating}
                    onClick={() => {
                      setPageError(null);
                      void withPin(() => archiveMut.mutateAsync());
                    }}
                  >
                    {archiveMut.isPending ? '...' : 'Archive'}
                  </Btn>
                )}
                {!sectionReadOnly && account.status === 'archived' && (
                  <Btn
                    size="sm"
                    variant="secondary"
                    disabled={mutating}
                    onClick={() => {
                      setPageError(null);
                      void withPin(() => unarchiveMut.mutateAsync());
                    }}
                  >
                    {unarchiveMut.isPending ? '...' : 'Unarchive'}
                  </Btn>
                )}
              </>
            }
          />
        </div>

        <div className="hidden items-center justify-between gap-3 md:flex">
          <Link
            to="/match/accounts"
            className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-border/70 bg-surface px-3 py-1.5 text-small font-medium text-ink-muted shadow-sm backdrop-blur transition-colors hover:bg-surface-raised hover:text-ink"
          >
            <span aria-hidden>←</span>
            <span>All match accounts</span>
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <Btn size="sm" variant="secondary" onClick={() => nav(`/match/accounts/${id}/ledger`)}>
              <ScrollText className="h-4 w-4" strokeWidth={2} />
              <span>Full ledger</span>
            </Btn>
            {!sectionReadOnly && account.status === 'active' && (
              <Btn
                size="sm"
                variant="secondary"
                disabled={mutating}
                onClick={() => {
                  setPageError(null);
                  void withPin(() => archiveMut.mutateAsync());
                }}
              >
                {archiveMut.isPending ? 'Working…' : 'Archive'}
              </Btn>
            )}
            {!sectionReadOnly && account.status === 'archived' && (
              <Btn
                size="sm"
                variant="secondary"
                disabled={mutating}
                onClick={() => {
                  setPageError(null);
                  void withPin(() => unarchiveMut.mutateAsync());
                }}
              >
                {unarchiveMut.isPending ? 'Working…' : 'Unarchive'}
              </Btn>
            )}
          </div>
        </div>

        <MetricStrip
          section="match"
          className="hidden md:grid"
          items={[
            {
              label: 'Balance',
              value: balance,
              note: 'Current match balance',
              tone: balance.startsWith('-') ? 'neg' : balance === '0' ? 'default' : 'pos',
            },
            {
              label: 'Configured',
              value: `${configuredPct}%`,
              note: 'split coverage',
              tone: configuredPct > 100 ? 'neg' : configuredPct === 0 ? 'warn' : 'default',
            },
            {
              label: 'Remainder',
              value: `${remainderPct}%`,
              note: 'stays on source',
              tone: remainderPct === 0 ? 'default' : 'warn',
            },
          ]}
        />

        {pageError && <ErrorBanner>{pageError}</ErrorBanner>}

        <div className="md:hidden">
          <ActionQueueCard
            section="match"
            eyebrow="Account desk"
            title={
              account.status === 'active'
                ? 'Split rules are live for new entries'
                : 'This account is archived and locked for edits'
            }
            description={
              sectionReadOnly
                ? 'Boss mode stays view only here. Review the routing, then ask the owner to change it if needed.'
                : readOnly
                ? 'Unarchive the account before editing split rules or routing targets.'
                : 'Adjust routing percentages here before the next match entry is posted.'
            }
            tone={account.status === 'active' ? 'accent' : 'neutral'}
            meta={
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <span>Linked party</span>
                  <span className="font-medium text-ink">
                    {linkedParty ? linkedParty.displayName : 'Not linked'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>Routing</span>
                  <span className="font-medium text-ink">
                    {configuredPct}% split / {remainderPct}% remainder
                  </span>
                </div>
                <div className="text-[12px] leading-5 text-ink-muted">
                  {account.notes?.trim() || 'No notes added for this account yet.'}
                </div>
              </div>
            }
            actions={
              <>
                <Btn size="sm" variant="secondary" onClick={() => nav('/match/accounts')}>
                  All accounts
                </Btn>
                {linkedParty && (
                  <Btn size="sm" variant="secondary" onClick={() => nav(`/parties/${linkedParty.id}/ledger`)}>
                    Party ledger
                  </Btn>
                )}
              </>
            }
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <AboutCard
            account={account}
            linkedParty={linkedParty ?? null}
            sectionReadOnly={sectionReadOnly}
            mutating={mutating}
            onOpenLedger={() => nav(`/match/accounts/${id}/ledger`)}
            onArchive={() => {
              setPageError(null);
              void withPin(() => archiveMut.mutateAsync());
            }}
            onUnarchive={() => {
              setPageError(null);
              void withPin(() => unarchiveMut.mutateAsync());
            }}
          />

          <SplitsEditor
            accountId={id}
            readOnly={readOnly}
            initialSplits={splits}
            candidates={(accountsQ.data ?? []).filter((a) => a.id !== id && a.status === 'active')}
            onSaved={() => {
              setPageError(null);
              setSavedNotice(true);
              setTimeout(() => setSavedNotice(false), 1800);
              void qc.invalidateQueries({ queryKey: ['match-account', id] });
              void qc.invalidateQueries({ queryKey: ['match-balances'] });
            }}
            onError={(msg) => setPageError(msg)}
            withPin={withPin}
            savedNotice={savedNotice}
          />
        </div>

        <SectionCard
          section="match"
          padded={false}
          title="Recent posting activity"
          action={
            <Link
              to={`/match/accounts/${id}/ledger`}
              className="text-caption font-medium text-sec-match hover:underline"
            >
              Open full ledger
            </Link>
          }
        >
          <RecentActivity
            rows={recentQ.data?.rows ?? []}
            loading={recentQ.isLoading}
            accountsById={accountsById}
          />
        </SectionCard>
      </div>
    </MatchShell>
  );
}

function AboutCard({
  account,
  linkedParty,
  sectionReadOnly,
  mutating,
  onOpenLedger,
  onArchive,
  onUnarchive,
}: {
  account: MatchAccount;
  linkedParty: Party | null;
  sectionReadOnly: boolean;
  mutating: boolean;
  onOpenLedger: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
}) {
  return (
    <SectionCard section="match" title="Account info">
      <div className="flex flex-wrap items-center gap-2">
        <StatePill state={account.status} />
        {linkedParty && <Pill tone="indigo">{linkedParty.role}</Pill>}
      </div>

      <div className="mt-4 space-y-2 rounded-[20px] bg-surface-raised/70 p-3 ring-1 ring-border/50">
        <InfoRow label="Name" value={account.name} />
        <InfoRow label="Linked party" value={linkedParty ? linkedParty.displayName : 'Not linked'} />
        <InfoRow label="Created" value={account.createdAt} mono />
      </div>

      <div className="mt-4 rounded-[20px] border border-border/70 bg-surface-raised p-3">
        <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-ink-muted">
          Notes
        </div>
        <div className="mt-2 whitespace-pre-wrap text-small leading-6 text-ink">
          {account.notes?.trim() || 'No notes added for this account yet.'}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Btn size="sm" variant="secondary" onClick={onOpenLedger}>
          <ScrollText className="h-4 w-4" strokeWidth={2} />
          <span>Full ledger</span>
        </Btn>
        {linkedParty && (
          <Link
            to={`/parties/${linkedParty.id}/ledger`}
            className="inline-flex min-h-[40px] items-center gap-1.5 rounded-[14px] border border-border/70 bg-surface px-3.5 py-2 text-small font-medium text-sec-match shadow-sm backdrop-blur transition-colors hover:bg-surface-raised"
          >
            <span>{linkedParty.displayName}</span>
            <ExternalLink className="h-3.5 w-3.5" strokeWidth={2} />
          </Link>
        )}
        {!sectionReadOnly && account.status === 'active' && (
          <Btn size="sm" variant="secondary" disabled={mutating} onClick={onArchive}>
            {mutating ? 'Working…' : 'Archive'}
          </Btn>
        )}
        {!sectionReadOnly && account.status === 'archived' && (
          <Btn size="sm" variant="secondary" disabled={mutating} onClick={onUnarchive}>
            {mutating ? 'Working…' : 'Unarchive'}
          </Btn>
        )}
      </div>
    </SectionCard>
  );
}

function InfoRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="text-[12px] text-ink-muted">{label}</div>
      <div className={mono ? 'font-mono text-[12px] text-ink' : 'text-small font-medium text-ink'}>
        {value}
      </div>
    </div>
  );
}

function makeKey(): string {
  return Math.random().toString(36).slice(2, 10);
}

function toDraft(s: MatchSplit): DraftSplit {
  return {
    key: makeKey(),
    targetMatchAccountId: s.targetMatchAccountId,
    label: s.label,
    percentage: s.percentage,
  };
}

function SplitsEditor({
  accountId,
  readOnly,
  initialSplits,
  candidates,
  onSaved,
  onError,
  withPin,
  savedNotice,
}: {
  accountId: number;
  readOnly: boolean;
  initialSplits: MatchSplit[];
  candidates: MatchAccount[];
  onSaved: () => void;
  onError: (msg: string) => void;
  withPin: ReturnType<typeof usePinGate>['withPin'];
  savedNotice: boolean;
}) {
  const [drafts, setDrafts] = useState<DraftSplit[]>(() => initialSplits.map(toDraft));
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setDrafts(initialSplits.map(toDraft));
    setLocalError(null);
  }, [initialSplits]);

  const saveMut = useMutation({
    mutationFn: (payload: SplitReplaceInput) => match.replaceSplits(accountId, payload),
    onSuccess: () => {
      onSaved();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 423) return;
      onError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const sumPct = useMemo(() => {
    let s = 0;
    for (const d of drafts) {
      const n = Number(d.percentage);
      if (Number.isFinite(n)) s += n;
    }
    return Math.round(s * 1e6) / 1e6;
  }, [drafts]);
  const remainder = Math.round((100 - sumPct) * 1e6) / 1e6;
  const overCap = sumPct > 100;

  const addRow = () => {
    setDrafts((prev) => [
      ...prev,
      {
        key: makeKey(),
        targetMatchAccountId: null,
        label: '',
        percentage: '',
      },
    ]);
  };

  const removeRow = (key: string) => {
    setDrafts((prev) => prev.filter((d) => d.key !== key));
  };

  const moveRow = (key: string, dir: -1 | 1) => {
    setDrafts((prev) => {
      const idx = prev.findIndex((d) => d.key === key);
      if (idx < 0) return prev;
      const nextIdx = idx + dir;
      if (nextIdx < 0 || nextIdx >= prev.length) return prev;
      const copy = prev.slice();
      const [item] = copy.splice(idx, 1);
      if (item === undefined) return prev;
      copy.splice(nextIdx, 0, item);
      return copy;
    });
  };

  const updateRow = (key: string, patch: Partial<DraftSplit>) => {
    setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  };

  const onCancel = () => {
    setDrafts(initialSplits.map(toDraft));
    setLocalError(null);
  };

  const onSave = () => {
    setLocalError(null);

    if (readOnly) {
      setLocalError('This account is read-only right now. Unarchive it or switch out of boss mode to edit splits.');
      return;
    }

    for (const [i, d] of drafts.entries()) {
      if (d.label.trim().length === 0) {
        setLocalError(`Row ${i + 1}: label is required.`);
        return;
      }
      if (d.targetMatchAccountId === null) {
        setLocalError(`Row ${i + 1}: target account is required.`);
        return;
      }
      const n = Number(d.percentage);
      if (!Number.isFinite(n) || n <= 0) {
        setLocalError(`Row ${i + 1}: percentage must be > 0.`);
        return;
      }
      if (n > 100) {
        setLocalError(`Row ${i + 1}: percentage cannot exceed 100.`);
        return;
      }
    }

    if (sumPct > 100) {
      setLocalError(`Sum of splits is ${sumPct}% — it must stay at 100% or less.`);
      return;
    }

    const payload: SplitReplaceInput = {
      splits: drafts.map((d) => ({
        targetMatchAccountId: d.targetMatchAccountId as number,
        label: d.label.trim(),
        percentage: String(Number(d.percentage)),
      })),
    };

    void withPin(() => saveMut.mutateAsync(payload));
  };

  const hasChanges = useMemo(() => {
    if (drafts.length !== initialSplits.length) return true;
    for (let i = 0; i < drafts.length; i += 1) {
      const d = drafts[i];
      const s = initialSplits[i];
      if (!d || !s) return true;
      if (
        d.label !== s.label ||
        d.targetMatchAccountId !== s.targetMatchAccountId ||
        Number(d.percentage) !== Number(s.percentage)
      ) {
        return true;
      }
    }
    return false;
  }, [drafts, initialSplits]);

  const targetOptions = [
    { value: '', label: '— select account —' },
    ...candidates.map((a) => ({
      value: String(a.id),
      label: a.name,
    })),
  ];

  return (
    <SectionCard
      section="match"
      title="Split rules"
      action={
        savedNotice ? <span className="text-[11px] text-pos">saved ✓</span> : undefined
      }
    >
      <div className="rounded-[20px] bg-surface-raised/70 p-3 ring-1 ring-border/50">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-ink-muted">
              Allocation
            </div>
            <div className="mt-1 text-small text-ink">
              {drafts.length === 0
                ? 'No splits configured. New entries stay fully on the source account.'
                : `${sumPct}% routes to split accounts and ${overCap ? '—' : `${remainder}%`} stays on the source account.`}
            </div>
          </div>
          <Pill tone={overCap ? 'red' : remainder === 0 ? 'neutral' : 'amber'}>
            {overCap ? 'Over 100%' : `${overCap ? '—' : remainder}% remainder`}
          </Pill>
        </div>
      </div>

      {readOnly && (
        <div className="mt-3 rounded-[18px] border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          This account is read-only. Unarchive it, or switch out of boss mode, before editing split rules.
        </div>
      )}

      {drafts.length === 0 ? (
        <div className="mt-3">
          <EmptyState
            section="match"
            icon={ScrollText}
            title="No split rules yet"
            description="Every new entry stays 100% on this account until you add at least one split rule."
          />
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {drafts.map((d, i) => (
            <SplitRow
              key={d.key}
              index={i}
              total={drafts.length}
              draft={d}
              targetOptions={targetOptions}
              readOnly={readOnly}
              onChange={(patch) => updateRow(d.key, patch)}
              onRemove={() => removeRow(d.key)}
              onMoveUp={() => moveRow(d.key, -1)}
              onMoveDown={() => moveRow(d.key, 1)}
            />
          ))}
        </div>
      )}

      {!readOnly && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="ghost" onClick={addRow}>
            + Add split
          </Button>
          <div className="flex-1" />
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={!hasChanges || saveMut.isPending}>
            Reset
          </Button>
          <Button size="sm" onClick={onSave} disabled={saveMut.isPending || overCap}>
            {saveMut.isPending ? <Spinner /> : 'Save rules'}
          </Button>
        </div>
      )}

      {localError && (
        <div className="mt-3">
          <ErrorBanner>{localError}</ErrorBanner>
        </div>
      )}
    </SectionCard>
  );
}

function SplitRow({
  index,
  total,
  draft,
  targetOptions,
  readOnly,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  index: number;
  total: number;
  draft: DraftSplit;
  targetOptions: { value: string; label: string }[];
  readOnly: boolean;
  onChange: (patch: Partial<DraftSplit>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const firstRow = index === 0;
  const lastRow = index === total - 1;

  return (
    <div className="rounded-[22px] border border-border/70 app-shell-surface p-3 shadow-sm backdrop-blur">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-ink-muted">
            Split {index + 1}
          </div>
          <div className="mt-1 text-small font-medium text-ink">
            Route part of each entry to another account
          </div>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onMoveUp}
              disabled={firstRow}
              aria-label="Move up"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-surface text-ink transition-colors hover:bg-surface-raised disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ArrowUp className="h-4 w-4" strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={onMoveDown}
              disabled={lastRow}
              aria-label="Move down"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-surface text-ink transition-colors hover:bg-surface-raised disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ArrowDown className="h-4 w-4" strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={onRemove}
              aria-label="Delete row"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-surface text-neg transition-colors hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1.2fr_0.7fr]">
        <TextInput
          label="Label"
          value={draft.label}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="e.g. paati"
          disabled={readOnly}
        />
        <Select
          label="Target account"
          value={draft.targetMatchAccountId === null ? '' : String(draft.targetMatchAccountId)}
          onChange={(v) =>
            onChange({
              targetMatchAccountId: v === '' ? null : Number(v),
            })
          }
          options={targetOptions}
        />
        <TextInput
          label="Percentage"
          value={draft.percentage}
          onChange={(e) => onChange({ percentage: e.target.value })}
          type="number"
          inputMode="decimal"
          placeholder="30"
          disabled={readOnly}
          hint="0 < % ≤ 100"
        />
      </div>
    </div>
  );
}

function RecentActivity({
  rows,
  loading,
  accountsById,
}: {
  rows: MatchLedgerRow[];
  loading: boolean;
  accountsById: Map<number, MatchAccount>;
}) {
  if (loading) {
    return (
      <div className="p-4">
        <RowSkeleton rows={3} />
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="p-4">
        <EmptyState
          section="match"
          icon={ScrollText}
          title="No posting activity yet"
          description="Once entries are posted to this account, the latest routing history will appear here."
        />
      </div>
    );
  }
  return (
    <>
      <div className="space-y-3 p-4 md:hidden">
        {rows.map((row) => {
          const sourceAccount = accountsById.get(row.source_match_account_id);
          const voided = row.voided_at !== null;
          return (
            <TimelineRow
              key={row.id}
              section="match"
              title={row.label ?? 'Remainder'}
              subtitle={row.note?.trim() || `${row.part_kind === 'split' ? 'Split' : 'Remainder'} from ${sourceAccount?.name ?? `#${row.source_match_account_id}`}`}
              date={row.effective_date}
              amount={row.amount}
              amountTone={Number(row.amount) >= 0 ? 'pos' : 'neg'}
              badges={
                <>
                  <Pill tone={row.part_kind === 'split' ? 'amber' : 'neutral'}>
                    {row.part_kind}
                  </Pill>
                  {voided && <Pill tone="neutral">voided</Pill>}
                </>
              }
              action={
                <Link
                  to={`/match/accounts/${row.source_match_account_id}/ledger`}
                  className="text-[11px] font-medium text-sec-match"
                >
                  Source
                </Link>
              }
            />
          );
        })}
      </div>

      <div className="hidden overflow-x-auto md:block">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="border-b border-border text-left text-[10px] uppercase tracking-wide text-ink-muted">
              <th className="px-4 py-2.5">Date</th>
              <th className="px-4 py-2.5">Label / kind</th>
              <th className="px-4 py-2.5">Source</th>
              <th className="px-4 py-2.5 text-right">Amount</th>
              <th className="px-4 py-2.5">Note</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row) => {
              const sourceAccount = accountsById.get(row.source_match_account_id);
              const voided = row.voided_at !== null;
              return (
                <tr key={row.id} className={voided ? 'text-ink-muted line-through' : ''}>
                  <td className="px-4 py-2.5 font-mono text-ink-muted">{row.effective_date}</td>
                  <td className="px-4 py-2.5">
                    <span className="text-ink">{row.label ?? <span className="italic text-ink-muted">remainder</span>}</span>
                    <span className="ml-2 text-[10px] uppercase text-ink-muted">{row.part_kind}</span>
                  </td>
                  <td className="px-4 py-2.5 text-ink-muted">{sourceAccount?.name ?? `#${row.source_match_account_id}`}</td>
                  <td
                    className={[
                      'px-4 py-2.5 text-right font-mono',
                      Number(row.amount) >= 0 ? 'text-pos' : 'text-neg',
                    ].join(' ')}
                  >
                    {row.amount}
                  </td>
                  <td className="px-4 py-2.5 text-ink-muted">{row.note ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
